import { useState } from "react";

import Spinner from "../../../shared/ui/Loading/Spinner";
import {
  DEAL_REASON_MAX_LENGTH,
  getDealAvailableActions,
  getDealLifecycleAction,
  getTerminalDealStatusMessage,
  isTerminalDealStatus,
  validateCancelReason,
} from "../lib/dealLifecycleActions";
import { getDealStatusLabel } from "../lib/dealStatusLabels";

function DealStatusActions({
  deal,
  pendingActionId,
  isTransitioning,
  statusMessage,
  statusMessageTone,
  errorMessage,
  fieldErrors,
  onSelectAction,
  onCancelConfirmation,
  onConfirmAction,
  onReasonDraftChange,
}) {
  const actions = getDealAvailableActions(deal);
  const pendingAction = getDealLifecycleAction(pendingActionId);
  const terminalMessage = getTerminalDealStatusMessage(deal.status);
  const [reasonDraft, setReasonDraft] = useState("");
  const [localReasonError, setLocalReasonError] = useState(null);

  const handleSelect = (actionId) => {
    setReasonDraft("");
    setLocalReasonError(null);
    onSelectAction(actionId);
  };

  const handleCancel = () => {
    if (isTransitioning) return;
    setReasonDraft("");
    setLocalReasonError(null);
    onCancelConfirmation();
  };

  const handleReasonChange = (event) => {
    const next = event.target.value;
    setReasonDraft(next);
    setLocalReasonError(null);
    if (typeof onReasonDraftChange === "function") {
      onReasonDraftChange(next);
    }
  };

  const handleConfirm = () => {
    if (!pendingAction || isTransitioning) return;
    if (pendingAction.reasonRequired) {
      const validated = validateCancelReason(reasonDraft);
      if (!validated.ok) {
        setLocalReasonError(validated.message);
        return;
      }
      setLocalReasonError(null);
      onConfirmAction(pendingAction.id, validated.reason);
      return;
    }
    onConfirmAction(pendingAction.id, reasonDraft);
  };

  const isTerminal = isTerminalDealStatus(deal.status);

  return (
    <section
      className={[
        "feature-deal-detail__section",
        isTerminal ? "feature-deal-detail__section--terminal" : null,
      ]
        .filter(Boolean)
        .join(" ")}
      aria-labelledby="deal-status-actions-heading"
      aria-busy={isTransitioning}
      data-testid="deal-lifecycle-actions"
    >
      <h2 id="deal-status-actions-heading">Дії за угодою</h2>

      {isTerminal ? (
        <div
          className="feature-deal-detail__terminal-banner"
          role="note"
          data-testid="deal-terminal-status"
        >
          <span
            className={[
              "feature-deals-list__status-badge",
              `feature-deals-list__status-badge--${deal.status}`,
            ].join(" ")}
          >
            {getDealStatusLabel(deal.status)}
          </span>
          {terminalMessage ? (
            <p className="feature-deal-detail__status-terminal">{terminalMessage}</p>
          ) : null}
        </div>
      ) : (
        <p className="feature-deal-detail__status-current">
          Поточний статус: <strong>{getDealStatusLabel(deal.status)}</strong>
        </p>
      )}

      {!isTerminal && actions.length > 0 ? (
        <div className="feature-deal-detail__status-actions">
          {actions.map((action) => (
            <button
              key={action.id}
              type="button"
              className={[
                "action-btn",
                action.destructive ? "action-btn--danger" : null,
              ]
                .filter(Boolean)
                .join(" ")}
              onClick={() => handleSelect(action.id)}
              disabled={isTransitioning || pendingActionId != null}
              aria-pressed={pendingActionId === action.id}
            >
              {action.label}
            </button>
          ))}
        </div>
      ) : null}

      {!isTerminal && actions.length === 0 ? (
        <p className="feature-deal-detail__status-terminal" role="note">
          Для вашої ролі зараз немає доступних дій.
        </p>
      ) : null}

      {pendingAction ? (
        <div
          className="feature-deal-detail__status-confirm"
          role="region"
          aria-labelledby="deal-status-confirm-heading"
        >
          <h3 id="deal-status-confirm-heading">{pendingAction.label}</h3>
          <p>
            Поточний статус:{" "}
            <strong>{getDealStatusLabel(deal.status)}</strong>
          </p>
          <p>
            Новий статус:{" "}
            <strong>{getDealStatusLabel(pendingAction.targetStatus)}</strong>
          </p>
          {pendingAction.consequence ? <p>{pendingAction.consequence}</p> : null}

          {(pendingAction.reasonRequired
            || pendingAction.id === "decline"
            || pendingAction.id === "withdraw") ? (
            <label className="feature-deal-detail__reason-field">
              <span>
                {pendingAction.reasonRequired
                  ? "Причина скасування (обовʼязково)"
                  : "Причина (необовʼязково)"}
              </span>
              <textarea
                value={reasonDraft}
                onChange={handleReasonChange}
                maxLength={DEAL_REASON_MAX_LENGTH}
                rows={3}
                disabled={isTransitioning}
                aria-required={pendingAction.reasonRequired}
                aria-invalid={Boolean(localReasonError || (fieldErrors && fieldErrors.reason))}
              />
            </label>
          ) : null}

          {localReasonError ? (
            <p className="feature-deal-detail__status-error" role="alert">
              {localReasonError}
            </p>
          ) : null}
          {fieldErrors && fieldErrors.reason ? (
            <p className="feature-deal-detail__status-error" role="alert">
              {fieldErrors.reason}
            </p>
          ) : null}

          <div className="feature-deal-detail__status-confirm-actions">
            <button
              type="button"
              className={[
                "action-btn",
                pendingAction.destructive ? "action-btn--danger" : null,
              ]
                .filter(Boolean)
                .join(" ")}
              onClick={handleConfirm}
              disabled={isTransitioning}
            >
              {isTransitioning ? (
                <Spinner size="sm" label="Оновлення статусу" />
              ) : (
                "Підтвердити"
              )}
            </button>
            <button
              type="button"
              className="action-btn"
              onClick={handleCancel}
              disabled={isTransitioning}
            >
              Не змінювати
            </button>
          </div>
        </div>
      ) : null}

      {isTransitioning ? (
        <p className="feature-deal-detail__status-progress" aria-live="polite">
          Оновлення статусу…
        </p>
      ) : null}

      {statusMessage ? (
        <p
          className={[
            "feature-deal-detail__status-result",
            statusMessageTone === "error"
              ? "feature-deal-detail__status-result--error"
              : null,
          ]
            .filter(Boolean)
            .join(" ")}
          role={statusMessageTone === "error" ? "alert" : "status"}
          aria-live="polite"
        >
          {statusMessage}
        </p>
      ) : null}

      {errorMessage ? (
        <p className="feature-deal-detail__status-error" role="alert">
          {errorMessage}
        </p>
      ) : null}
    </section>
  );
}

export default DealStatusActions;
